/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import {Map} from 'immutable';
import {setUpStore, callTargetedAction} from 'client/framework';
import {addGenericErrorToast, addToast, Text, TextArea} from '@albert-io/atomic';

import {convertDateAndTimeToMomentObject} from 'lib/timeUtils';

import {history} from 'client/history';
import appStore from 'client/AppStore';
import {SETTINGS as correctAnswerSettings} from 'client/Assignments/constants';
import {ConfirmChangesModal} from 'client/generic/ConfirmChangesModal/ConfirmChangesModal.react';
import {confirmChangesModalActions} from 'client/generic/ConfirmChangesModal/ConfirmChangesModal.actions';
import TimeLimitTooltip from 'client/CreateAssignmentV2/shared/TimeLimitTooltip.react';
import {confirmChangesRules} from 'client/CreateAssignmentV2/shared/utils';
import createAssignmentStore from 'client/CreateAssignmentV2/CreateAssignment.store';

import TextInput from 'generic/Forms/TextInput/TextInput.react';
import DateTimePicker from 'generic/DateTimePicker/DateTimePicker.react';
import Checkbox from 'generic/Forms/Checkbox/Checkbox.react';
import RadioGroup from 'generic/Forms/RadioGroup/RadioGroup.react';
import NumberInput from 'client/generic/Forms/NumberInput/NumberInput.react';
import Button from 'sg/Button/Button.react';

import AssignmentSettingsEditorStore from './AssignmentSettingsEditor.store';
import assignmentSettingsEditorActions from './AssignmentSettingsEditor.actions';

import './assignment-settings-editor.scss';

const confirmChangesModalStoreName = 'AssignmentSettingsEditorConfirmChangesModalStoreName';
const windowConfirmStoreName = 'AssignmentSettingsEditorWindowConfirmStoreName';

export default class AssignmentSettingsEditor extends React.Component {
  static propTypes = {
    assignmentId: PropTypes.string.isRequired,
    type: PropTypes.string
  };

  static defaultProps = {
    type: 'Assignment'
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    // Because we're saving the model to our store, we don't want the initial data to be set on the server,
    // or our assignment model will be clobbered and we'll initially get a Map() back. Therefore, we only
    // init this on the client.
    if (process.env.IS_BROWSER) {
      callTargetedAction({
        name: assignmentSettingsEditorActions.INITIALIZE,
        payload: this.props.assignmentId,
        targetStore: this.getStore().getName()
      });
    }
  }

  componentWillUnmount() {
    callTargetedAction({
      name: assignmentSettingsEditorActions.RESET_STORE,
      targetStore: this.getStore().getName()
    });
  }

  getStore = () => {
    return setUpStore(
      AssignmentSettingsEditorStore,
      `AssignmentSettingsEditorStore/${this.props.assignmentId}`
    );
  };

  render() {
    const assignment = this.getStore().getAssignment();
    if (!assignment) {
      return null;
    }
    return (
      <section className='assignment-settings-editor'>
        <h1 className='assignment-settings-editor__heading'>{this.props.type} Options</h1>
        <div className='assignment-settings-editor__subheading-wrapper'>
          <h2 className='assignment-settings-editor__assignment-name'>{assignment.getName()}</h2>
          <QuestionCountsByDifficulty questionCounts={assignment.getQuestionCountsByDifficulty()} />
        </div>
        <AssignmentEditor assignment={assignment} store={this.getStore()} type={this.props.type} />
      </section>
    );
  }
}

class QuestionCountsByDifficulty extends React.PureComponent {
  static propTypes = {
    questionCounts: PropTypes.shape({
      Easy: PropTypes.number,
      Moderate: PropTypes.number,
      Difficult: PropTypes.number
    })
  };

  render() {
    return (
      <ul className='question-counts-by-difficulty'>
        {Object.entries(this.props.questionCounts).map(([label, count]) => {
          const className = classnames(
            'question-counts-by-difficulty__item',
            `question-counts-by-difficulty__item--${label.toLowerCase()}`
          );
          return (
            <li className={className} key={label}>
              {label}: {count}
            </li>
          );
        })}
      </ul>
    );
  }
}

class SectionTimeLimitEditor extends React.Component {
  static propTypes = {
    errors: ImmutablePropTypes.map,
    sections: ImmutablePropTypes.list,
    updateTimeLimit: PropTypes.func
  };

  updateSection = (e) => {
    const payload = {
      sectionPosition: parseInt(e.target.dataset.sectionPosition, 10),
      timeLimit: e.target.value === '' ? null : parseInt(e.target.value, 10)
    };
    this.props.updateTimeLimit(payload);
  };

  render() {
    return (
      <fieldset className='u-border_none u-pad_0 u-mar-b_0 u-mar-lr_0 u-mar-t_2'>
        <Text as='legend' bold className='u-mar-b_1' color={null} size='s'>
          Section time limits (minutes)
        </Text>
        {this.props.sections.valueSeq().map((section) => {
          const value = section.getTimeLimit();
          const errorMessage = this.props.errors.get(section.getPosition(), null);
          return (
            <div key={section.getId()} className='assignment-settings-editor_time-limit'>
              <NumberInput
                autoCorrect
                error={!!errorMessage}
                errorMessage={errorMessage}
                label={
                  <span>
                    {section.getTitle()} time limit <TimeLimitTooltip />
                  </span>
                }
                min={1}
                onChange={this.updateSection}
                defaultValue={value}
                data-section-position={section.getPosition()}
              />
            </div>
          );
        })}
      </fieldset>
    );
  }
}

/**
 * AssignmentEditor
 * ===
 *
 * This component manages the sections' time limit states internally so we can do two things more easily:
 * - Track changes each individual section
 * - Manage errors that arise (and enable/disable submitting based on their presence)
 *
 */
class AssignmentEditor extends React.Component {
  static propTypes = {
    assignment: PropTypes.any,
    type: PropTypes.string,
    store: PropTypes.any
  };

  constructor(props) {
    super(props);

    this.state = {
      ready: false,
      sections: props.assignment.getSections().reduce((prev, curr) => {
        prev = prev.set(curr.getPosition(), curr);
        return prev;
      }, Map()),
      errors: props.assignment.getSections().reduce((prev, curr) => {
        const errorMessage = curr.validators.getTimeLimit()?.join(' ');

        if (errorMessage) {
          return prev.set(curr.getPosition(), errorMessage);
        }

        return prev;
      }, Map())
    };

    this.correctAnswerSettingsOptions = [
      {
        label: (
          <span>
            After {props.type.toLowerCase()} completion <span>(moderately restrictive)</span>
          </span>
        ),
        value: correctAnswerSettings.SHOW_AFTER_ALL
      },
      {
        label: (
          <span>
            After due date has passed <span>(most restrictive)</span>
          </span>
        ),
        value: correctAnswerSettings.SHOW_AFTER_DUE_DATE
      }
    ];
  }

  componentDidMount() {
    if (!this.props.assignment.hasStartDate()) {
      this.updateField('setStartDate', null);
    }
    this.setState({ready: true});
  }

  updateField(setter, value) {
    callTargetedAction({
      name: assignmentSettingsEditorActions.UPDATE_FIELD,
      payload: {setter, value},
      targetStore: this.props.store.getName()
    });
  }

  updateTitle = (e) => {
    this.updateField('setName', e.target.value);
  };

  updateStartDate = ({date, time}) => {
    const startDate = !date ? 'now' : convertDateAndTimeToMomentObject({date, time});
    this.updateField('setStartDate', startDate);
  };

  updateDueDate = ({date, time}) => {
    const dueDate = convertDateAndTimeToMomentObject({date, time});
    this.updateField('setDueDate', dueDate);
  };

  toggleAllowsLateSubmissions = (e) => {
    this.updateField('setAllowLateSubmissions', e.target.checked);
  };

  updateCorrectAnswerSetting = (e) => {
    this.updateField('setCorrectAnswerSetting', e.target.value);
  };

  updateMessage = (e) => {
    this.updateField('setMessage', e.target.value);
  };

  navigateToClassroom = () => {
    history.pushState(
      null,
      appStore.routerProps.location.pathname
        .split('/')
        .slice(0, -1)
        .join('/')
    );
  };

  onSaveClick = () => {
    callTargetedAction({
      name: confirmChangesModalActions.TOGGLE_MODAL,
      targetStore: confirmChangesModalStoreName,
      payload: true
    });
  };

  /**
   * Logic wasn't added to the store to not add complexity to how it works.
   * @todo remove the editing store and move the logic into this file.
   */
  saveAssignment = async () => {
    if (this.props.assignment.validators.hasValidationErrors()) {
      return;
    }
    callTargetedAction({
      name: assignmentSettingsEditorActions.SAVE_ASSIGNMENT,
      targetStore: this.props.store.getName()
    });

    try {
      await this.props.store.getSaveAssignmentPromise();
      await this.saveTimeLimit();
      addToast({
        message: 'Your assessment was successfully edited',
        title: 'Success!',
        type: 'success'
      });
      this.navigateToClassroom();
    } catch (err) {
      logger.error('Failed saving edited assessment: %s', err.message || 'unknown error');
      addGenericErrorToast();
    }
  };

  handleTimeLimitChange = ({sectionPosition, timeLimit}) => {
    this.setState(({sections, errors}) => {
      const position = parseInt(sectionPosition, 10);

      const updatedSections = sections.updateIn([position], (section) =>
        section.setTimeLimit(timeLimit)
      );

      const updatedErrors = errors.update((prevErrors) => {
        const errorMessage = createAssignmentStore.getSectionTimeLimitError(timeLimit);
        return !errorMessage ? prevErrors.delete(position) : prevErrors.set(position, errorMessage);
      });

      return {
        sections: updatedSections,
        errors: updatedErrors
      };
    });
  };

  saveTimeLimit = async () => {
    const {sections} = this.state;
    for (const section of sections.valueSeq()) {
      // eslint-disable-next-line no-await-in-loop
      await section.save();
    }
  };

  render() {
    /**
     * Wait one render cycle to clear the start date
     * @see componentDidMount
     */

    if (!this.state.ready) {
      return null;
    }
    const {type, store} = this.props;
    const assignment = store.getAssignment();
    const titleError = assignment.validators.getName();
    const startDateError = assignment.validators.getStartDate();
    const startTimeError = assignment.validators.getStartTime();
    const dueDateError = assignment.validators.getDueDate();
    return (
      <div className='assignment-editor'>
        <div className='card'>
          <div className='assignment-editor__row a-form-row'>
            <TextInput
              required
              label={`${type} Title`}
              value={assignment.getName()}
              onChange={this.updateTitle}
              error={titleError !== null}
              errorMessage={titleError ? titleError.first() : null}
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <DateTimePicker
              customDefaultTime='06:00'
              dateError={startDateError ? startDateError.first() : null}
              dateLabel='Start Date'
              timeError={startTimeError}
              timeLabel='Start Time'
              defaultValue={assignment.getStartDate()}
              placeholder='Assign Immediately'
              onChange={this.updateStartDate}
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <DateTimePicker
              required
              dateError={dueDateError ? dueDateError.first() : null}
              dateLabel='Due Date'
              customDefaultTime='23:59'
              timeLabel='Due Time'
              defaultValue={assignment.getDueDate()}
              onChange={this.updateDueDate}
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <SectionTimeLimitEditor
              updateTimeLimit={this.handleTimeLimitChange}
              sections={this.state.sections}
              errors={this.state.errors}
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <Checkbox
              label='Allow late submissions'
              checked={assignment.isAllowLateSubmissions()}
              onChange={this.toggleAllowsLateSubmissions}
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <RadioGroup
              data={this.correctAnswerSettingsOptions}
              activeValue={assignment.getCorrectAnswerSetting()}
              onChange={this.updateCorrectAnswerSetting}
              label='Show correct answers and explanations:'
              name='correct-answer-setting'
            />
          </div>
          <div className='assignment-editor__row a-form-row'>
            <TextArea
              border
              className='assignment-editor__message'
              label='Message (optional)'
              value={assignment.getMessage()}
              onChange={this.updateMessage}
            />
          </div>
        </div>
        <div className='assignment-editor__actions-wrapper'>
          <Button linkButton text='Cancel' onClick={this.navigateToClassroom} />
          <Button
            color='green'
            text={`Save ${this.props.type}`}
            disabled={assignment.validators.hasValidationErrors() || !this.state.errors.isEmpty()}
            disabledTooltipContent='Please address all errors before saving.'
            onClick={this.onSaveClick}
          />
        </div>
        <ConfirmChangesModal
          oldResource={store.getOriginalAssignment()}
          onConfirm={this.saveAssignment}
          newResource={store.getAssignment()}
          rules={confirmChangesRules}
          storeName={confirmChangesModalStoreName}
          windowConfirmStoreName={windowConfirmStoreName}
        />
      </div>
    );
  }
}
